import { ComponentType, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { IconButton, Popover } from '@mui/material';
import { EllipsisVertical } from 'lucide-react';

import { noPropagation } from '@labradorsports/utils';

interface Option {
    text: string;
    onClick?: () => any;
    href?: string;
}

interface Props {
    options: Option[];
    getMenuPosition?: (menuHeight: number) => 'bottom' | 'top';
    icon?: string | ReactNode;
    DropdownComponent?: ComponentType<{
        optionClick: AnyFunction;
        options: Option[];
    }>;
    disablePortal?: boolean;
    dropdownProps?: any;
    containerRef?: RefObject<HTMLDivElement>;
}

const DropdownMenu: FC<Props> = ({
    options,
    getMenuPosition,
    icon,
    DropdownComponent,
    disablePortal = false,
    dropdownProps = {},
    containerRef,
}) => {
    const backupRef = useRef<HTMLDivElement>();
    const menuButtonRef = containerRef ?? backupRef;
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<'bottom' | 'top'>('bottom');
    const menuRef = useRef<HTMLDivElement>();

    useEffect(() => {
        const handler = () => {
            setOpen(false);
        };

        window.addEventListener('dropdown-opened', handler);

        return () => window.removeEventListener('dropdown-opened', handler);
    }, []);

    const onClick = (opt: Option) =>
        noPropagation((evt: any) => {
            if (opt.onClick) {
                opt.onClick();
                setOpen(false);
            }
        });

    const menuButtonClick = noPropagation(() => {
        window.dispatchEvent(new Event('dropdown-opened'));

        requestAnimationFrame(() => {
            setOpen(!open);

            requestAnimationFrame(() => {
                const menuHeight = menuRef.current?.offsetHeight;
                setPosition(getMenuPosition ? getMenuPosition(menuHeight) : 'bottom');
            });
        });
    });

    const iconElement = icon ? (
        typeof icon === 'string' ? (
            <img src={icon} onClick={menuButtonClick} alt="Dropdown menu icon" />
        ) : (
            icon
        )
    ) : (
        <EllipsisVertical />
    );

    const onClose = (evt: any, reason: string) => {
        setOpen(false);

        if (reason === 'backdropClick') {
            evt.stopPropagation();
        }
    };

    return (
        <div className={`dropdown-menu-container ${open ? 'menu-open' : ''}`} ref={menuButtonRef}>
            <IconButton className="dropdown-button" onClick={menuButtonClick}>
                {iconElement}
            </IconButton>
            {open ? (
                <Popover
                    open={open}
                    anchorEl={menuButtonRef.current}
                    onClose={onClose}
                    disablePortal={disablePortal}
                    anchorOrigin={{
                        vertical: position,
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: position === 'bottom' ? 'top' : 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <div className="dropdown-panel" ref={menuRef}>
                        {DropdownComponent ? (
                            <DropdownComponent optionClick={onClick} options={options} {...dropdownProps} />
                        ) : (
                            options.map((opt, idx) => (
                                <a key={idx} className="dropdown-menu-item" href={opt.href} onClick={onClick(opt)}>
                                    {opt.text}
                                </a>
                            ))
                        )}
                    </div>
                </Popover>
            ) : null}
        </div>
    );
};

export default DropdownMenu;
